import type { OpenRequestedDetail } from "@getuserfeedback/protocol/host";

export type OpenRequestedPayload = OpenRequestedDetail;

export type OpenRequestedDecision =
	| { allowed: true }
	| { allowed: false; reason: string };

type OpenRequestedSource = OpenRequestedPayload["source"];

export type OpenRequestedEvent = {
	/**
	 * Origin of this open request.
	 * - `"command"`: requested by an explicit open command, e.g. when you call `client.flow(flowId).open()`.
	 * - `"targeting"`: requested by targeting plan evaluation.
	 */
	readonly source: OpenRequestedSource;
	/** Flow identifier that is about to be opened. */
	readonly flowId: string;
	readonly defaultPrevented: boolean;
	preventDefault: () => void;
};

type OpenRequestListener = (event: OpenRequestedEvent) => void;

export type OpenRequestDispatcher = {
	subscribe: (listener: OpenRequestListener) => () => void;
	dispatch: (
		payload: OpenRequestedPayload,
		options?: {
			onListenerError?: (error: unknown) => void;
			preventHostDefault?: () => void;
		},
	) => OpenRequestedDecision;
};

export const createOpenRequestDispatcher = (): OpenRequestDispatcher => {
	const listeners = new Set<OpenRequestListener>();

	const subscribe = (listener: OpenRequestListener): (() => void) => {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	};

	const dispatch = (
		payload: OpenRequestedPayload,
		options?: {
			onListenerError?: (error: unknown) => void;
			preventHostDefault?: () => void;
		},
	): OpenRequestedDecision => {
		let prevented = false;
		const event: OpenRequestedEvent = {
			source: payload.source,
			flowId: payload.flowId,
			get defaultPrevented() {
				return prevented;
			},
			preventDefault: () => {
				prevented = true;
			},
		};

		for (const listener of listeners) {
			try {
				listener(event);
			} catch (error) {
				try {
					if (options?.onListenerError) {
						options.onListenerError(error);
					} else {
						console.error("Open request listener failed", error);
					}
				} catch {
					// Error reporting must not become open-request authority.
				}
			}
		}

		if (prevented) {
			options?.preventHostDefault?.();
			return {
				allowed: false,
				reason: "prevented_by_listener",
			};
		}
		return { allowed: true };
	};

	return {
		subscribe,
		dispatch,
	};
};
